import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Container, Row, Col } from 'react-bootstrap';
import { Input, Fieldset, BackLink, Card, Details, Button, Textarea, ErrorSummary } from 'nhsuk-react-components';
import { listAetiologies, createAetiology, updateAetiology, deleteAetiology, getAetiologyDetails } from '../../actions/aetiologyActions';
import { AETIOLOGY_CREATE_RESET, AETIOLOGY_UPDATE_RESET } from '../../constants/aetiologyConstants';
import Pagination from '../../components/pagination/Pagination.component';
import AppSpinner from '../../components/spinner/AppSpinner.component';
import Callout from '../../components/callout/Callout.component';
import Modal from '../../components/modal/Modal.component';
import PageWrapper from '../../components/layout/PageWrapper.component';

export default function AetiologyCRUDScreen({ history }) {
    const dispatch = useDispatch();

    useEffect(() => {
        document.title = "Edit Aetiologies";
    }, []);

    useEffect(() => {
        dispatch({ type: AETIOLOGY_CREATE_RESET });
        dispatch({ type: AETIOLOGY_UPDATE_RESET });
    }, [dispatch]);

    const [aetiologyListFilter, setaetiologyListFilter] = useState({ query: '', page: 1, filter: 'Name' });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newAetiology, setNewAetiology] = useState({ name: '', description: '' });
    const [updatedAetiology, setUpdatedAetiology] = useState({});

    const aetiologyList = useSelector((state) => state.aetiologyList);
    const { loading, error, aetiologies, currentPage, totalPages, totalCount } = aetiologyList;

    const aetiologyCreate = useSelector((state) => state.aetiologyCreate);
    const { success: successCreate, error: aetiologyCreateErrors, loading: aetiologyCreateLoading } = aetiologyCreate;

    const aetiologyUpdate = useSelector((state) => state.aetiologyUpdate);
    const { success: successUpdate, loading: aetiologyUpdateProcessing } = aetiologyUpdate;

    const aetiologyDetails = useSelector((state) => state.aetiologyDetails);
    const { aetiology: aetiologyDet, loading: aetiologyDetailsLoading } = aetiologyDetails;

    useEffect(() => {
        dispatch(listAetiologies(aetiologyListFilter));
    }, [dispatch, aetiologyListFilter]);

    useEffect(() => {
        if (aetiologyDet) {
            setUpdatedAetiology({ ...aetiologyDet });
        }
    }, [aetiologyDet]);

    useEffect(() => {
        if (successCreate) {
            setShowCreateModal(false);
            setNewAetiology({ name: '', description: '' });
            dispatch(listAetiologies(aetiologyListFilter));
            dispatch({ type: AETIOLOGY_CREATE_RESET });
        }
    }, [successCreate, dispatch, aetiologyListFilter]);

    const handlePageChange = (newPage) => {
        setaetiologyListFilter({ ...aetiologyListFilter, page: newPage });
    };

    const handleSearchChange = (e) => {
        setaetiologyListFilter({ ...aetiologyListFilter, query: e.target.value, page: 1 });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this aetiology?')) {
            dispatch(deleteAetiology(id)).then(() => {
                dispatch(listAetiologies(aetiologyListFilter));
                if (updatedAetiology?.id === id) {
                    setShowEditModal(false);
                    setUpdatedAetiology({});
                }
            });
        }
    };

    const handleEditAetiology = (id) => {
        dispatch(getAetiologyDetails(id));
        setShowCreateModal(false);
        setShowEditModal(true);
    };

    const handleCloseEdit = () => {
        setShowEditModal(false);
        setUpdatedAetiology({});
        dispatch({ type: AETIOLOGY_UPDATE_RESET });
    };

    const handleCreateAetiology = () => {
        dispatch(createAetiology(newAetiology));
    };

    const handleUpdateAetiology = () => {
        dispatch(updateAetiology(updatedAetiology.id, updatedAetiology)).then(() => {
            dispatch(getAetiologyDetails(updatedAetiology.id));
            dispatch(listAetiologies(aetiologyListFilter));
        });
    };

    const openCreateModal = () => {
        setNewAetiology({ name: '', description: '' });
        dispatch({ type: AETIOLOGY_CREATE_RESET });
        setShowEditModal(false);
        setShowCreateModal(true);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col md="12">
                            <div className="welcome-box">
                                <PageWrapper title="Aetiology Management" totalCount={totalCount} loading={loading} />
                                <BackLink
                                    asElement="a"
                                    href="#"
                                    onClick={() => history.goBack()}
                                >
                                    Return back
                                </BackLink>
                                <Button
                                    className="nhsuk-button--small nhsuk-u-margin-bottom-4"
                                    onClick={openCreateModal}
                                >
                                    + Create Aetiology
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <div className="nhsuk-grid-column-one-half">
                        <Card>
                            <Card.Content>
                                <Card.Heading className="nhsuk-heading-m">
                                    <Input
                                        id="search-aetiologies"
                                        label="Search aetiologies"
                                        name="search-aetiologies"
                                        type="search"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearchChange(e);
                                            }
                                        }}
                                    />
                                </Card.Heading>
                                <Card.Description>
                                    <Fieldset>
                                        {aetiologies && aetiologies.length > 0 ? (
                                            aetiologies.map((aetiology) => (
                                                <Details key={aetiology.id || aetiology._id}>
                                                    <Details.Summary>
                                                        {aetiology.name}
                                                    </Details.Summary>
                                                    <Details.Text>
                                                        <p>
                                                            <strong>Description:</strong> {aetiology.description}
                                                        </p>
                                                        <div className="compact-admin-table__actions">
                                                            <Button
                                                                className="nhsuk-button--small"
                                                                disabled={aetiologyDetailsLoading}
                                                                onClick={() => handleEditAetiology(aetiology.id)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                secondary
                                                                className="nhsuk-button--small btn-delete"
                                                                onClick={() => handleDelete(aetiology.id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </Details.Text>
                                                </Details>
                                            ))
                                        ) : null}
                                    </Fieldset>
                                    {loading ? (
                                        <AppSpinner />
                                    ) : error ? (
                                        <p>{error}</p>
                                    ) : (
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            handlePageChange={handlePageChange}
                                        />
                                    )}
                                </Card.Description>
                            </Card.Content>
                        </Card>
                    </div>

                    <div className="nhsuk-grid-column-one-half" />
                </Container>
            </div>

            <Modal
                show={showCreateModal}
                handleClose={() => {
                    setShowCreateModal(false);
                    dispatch({ type: AETIOLOGY_CREATE_RESET });
                }}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Create Aetiology</h2>
                {aetiologyCreateErrors && (
                    <ErrorSummary aria-labelledby="error-summary-title" role="alert" tabIndex="-1">
                        <ErrorSummary.Title id="error-summary-title">There is a problem</ErrorSummary.Title>
                        <ErrorSummary.Body>
                            <ul className="nhsuk-list nhsuk-list--bullet">
                                {aetiologyCreateErrors.map((error) => (
                                    <li key={error}>{error}</li>
                                ))}
                            </ul>
                        </ErrorSummary.Body>
                    </ErrorSummary>
                )}
                <Input
                    id="name-create"
                    label="Name"
                    name="name-create"
                    type="text"
                    onChange={(e) => setNewAetiology({ ...newAetiology, name: e.target.value })}
                    value={newAetiology?.name || ''}
                    required
                />
                <Textarea
                    id="description-create"
                    label="Description"
                    name="description-create"
                    onChange={(e) => setNewAetiology({ ...newAetiology, description: e.target.value })}
                    value={newAetiology?.description || ''}
                />
                <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                    <Button
                        className="nhsuk-button--small"
                        disabled={aetiologyCreateLoading || aetiologyUpdateProcessing}
                        onClick={handleCreateAetiology}
                    >
                        Create Aetiology
                    </Button>
                    <Button
                        secondary
                        className="nhsuk-button--small"
                        onClick={() => {
                            setShowCreateModal(false);
                            dispatch({ type: AETIOLOGY_CREATE_RESET });
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </Modal>

            <Modal
                show={showEditModal}
                handleClose={handleCloseEdit}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Edit Aetiology</h2>
                {aetiologyDetailsLoading ? (
                    <AppSpinner />
                ) : (
                    <>
                        <Input
                            id="name"
                            label="Name"
                            name="name"
                            type="text"
                            onChange={(e) => setUpdatedAetiology({ ...updatedAetiology, name: e.target.value })}
                            value={updatedAetiology.name ? updatedAetiology.name : ''}
                            required
                        />
                        <Textarea
                            id="description"
                            label="Description"
                            name="description"
                            onChange={(e) => setUpdatedAetiology({ ...updatedAetiology, description: e.target.value })}
                            value={updatedAetiology.description ? updatedAetiology.description : ''}
                        />
                        {successUpdate && (
                            <Callout
                                label="Success"
                                header="Success!"
                                autoDismiss={true}
                                dismissTime={5000}
                                time={new Date().toLocaleTimeString()}
                                title="Success!"
                                message="The aetiology was updated successfully."
                                mode="success"
                            />
                        )}
                        <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                            <Button
                                className="nhsuk-button--small"
                                disabled={aetiologyUpdateProcessing}
                                onClick={handleUpdateAetiology}
                            >
                                Save changes
                            </Button>
                            <Button
                                secondary
                                className="nhsuk-button--small"
                                onClick={handleCloseEdit}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </React.Fragment>
    );
}
